import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageTransition } from "@/components/PageTransition";
import { ChatButton } from "@/components/ChatButton";
import { SmoothScroll } from "@/components/SmoothScroll";
import { useSiteMemory } from "@/hooks/useSiteMemory";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useState, lazy, Suspense, useEffect } from "react";

// Lazy load ChatInterface to reduce initial bundle
const ChatInterface = lazy(() => import("@/components/ChatInterface").then(module => ({ default: module.ChatInterface })));

// Import Index directly (homepage - critical for initial load)
import Index from "./pages/Index";

// Lazy load secondary pages for code splitting
const Work = lazy(() => import("./pages/Work"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const QuickPicks = lazy(() => import("./pages/QuickPicks"));
const Services = lazy(() => import("./pages/Services"));
const Gallery = lazy(() => import("./pages/Gallery"));
const FeaturesPage = lazy(() => import("./pages/GeneratePromo"));
const GenerateRedirect = lazy(() => import("./pages/GeneratePromo").then(m => ({ default: m.GenerateRedirect })));

// PlayBack client portal
const PlaybackRoutes = lazy(() => import("./playback/PlaybackRoutes").then(m => ({ default: m.PlaybackRoutes })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppContent = () => {
  const { memory, setChatOpen, updateLastPage } = useSiteMemory();
  const [isChatOpen, setIsChatOpen] = useState(() => memory.chatOpen);
  const location = useLocation();
  const isFeaturesPage = location.pathname === "/features" || location.pathname === "/generate";
  const isPlaybackRoute = location.pathname.startsWith('/playback');

  // Sync chat state with memory
  const handleChatOpen = (open: boolean) => {
    setIsChatOpen(open);
    setChatOpen(open);
  };

  // Track page visits and clear any stale scroll locks
  useEffect(() => {
    updateLastPage(location.pathname);
    
    // Safety reset: clear any stale scroll locks from unmounted components
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.touchAction = '';
    document.body.style.width = '';
    document.body.style.top = '';
    document.documentElement.style.overflow = '';
  }, [location.pathname, updateLastPage]);

  // Preload ChatInterface on idle for faster subsequent opens
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(() => {
        import("@/components/ChatInterface").catch(() => {});
      });
    } else {
      setTimeout(() => {
        import("@/components/ChatInterface").catch(() => {});
      }, 2000);
    }
  }, []);

  // (isPlaybackRoute already declared above)

  return (
    <PageTransition>
      <div className="flex min-h-screen flex-col">
        {/* Only show marketing nav on non-playback routes */}
        {!isPlaybackRoute && <Navbar />}
        <main className={`flex-1 ${!isPlaybackRoute && !isFeaturesPage ? 'pb-20' : ''}`}>
          <Suspense fallback={<div className="min-h-screen bg-slate-950" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/work" element={<Work />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/generate" element={<GenerateRedirect />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/quick-picks" element={<QuickPicks />} />
              <Route path="/quick-picks/video" element={<QuickPicks />} />
              <Route path="/quick-picks/photo" element={<QuickPicks />} />
              <Route path="/services" element={<Services />} />
              <Route path="/playback/*" element={<PlaybackRoutes />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        {!isFeaturesPage && !isPlaybackRoute && <Footer />}
        {/* Global chat — appears on every route */}
        {!isChatOpen && <ChatButton onClick={() => handleChatOpen(true)} />}
        {isChatOpen && (
          <Suspense fallback={null}>
            <ChatInterface onClose={() => handleChatOpen(false)} />
          </Suspense>
        )}
      </div>
    </PageTransition>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <CartProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <LoadingScreen />
            <BrowserRouter>
              <SmoothScroll>
                <AppContent />
              </SmoothScroll>
            </BrowserRouter>
          </TooltipProvider>
        </CartProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
